import React, { useState, useEffect } from 'react';
import { useExpense } from '../../context/ExpenseContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { IndianRupee, TrendingUp, Receipt, Award, X } from 'lucide-react';
import { Button } from '../ui/button';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan-Mar)' },
  { value: 2, label: 'Q2 (Apr-Jun)' },
  { value: 3, label: 'Q3 (Jul-Sep)' },
  { value: 4, label: 'Q4 (Oct-Dec)' }
];

const CHART_COLORS = [
  '#0F52BA', '#FFC107', '#10B981', '#EF4444', '#8B5CF6', 
  '#F59E0B', '#06B6D4', '#EC4899', '#84CC16', '#6366F1'
];

const formatCurrency = (amount, currency = 'INR') => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const ExpenseDashboard = () => {
  const { getDashboardKPIs, getMonthlyTrend, getCategoryBreakdown, getTopExpenses, getCategoryDetails, isReady } = useExpense();
  
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(null);
  const [month, setMonth] = useState(null);
  
  const [kpis, setKpis] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [topExpenses, setTopExpenses] = useState([]);
  const [drillDownCategory, setDrillDownCategory] = useState(null);
  const [drillDownData, setDrillDownData] = useState([]);
  const [loading, setLoading] = useState(true);

  const years = [currentYear, currentYear - 1, currentYear - 2];

  // Bug 1 Fix: Only load dashboard data when auth is ready
  useEffect(() => {
    if (isReady) {
      loadDashboardData();
    }
  }, [isReady, year, quarter, month]);

  const loadDashboardData = async () => {
    setLoading(true);
    setDrillDownCategory(null);
    
    const [kpiData, trendData, categoryData, topData] = await Promise.all([
      getDashboardKPIs(year, quarter, month),
      getMonthlyTrend(year),
      getCategoryBreakdown(year, quarter, month),
      getTopExpenses(year, quarter, month, 5)
    ]);
    
    setKpis(kpiData);
    setMonthlyTrend(trendData);
    setCategoryBreakdown(categoryData);
    setTopExpenses(topData);
    setLoading(false);
  };

  const handleCategoryClick = async (data, index) => {
    console.log('Category clicked:', data, index);
    // Data from Bar onClick contains the full entry from categoryBreakdown
    if (data && data.category) {
      setDrillDownCategory(data.category);
      const details = await getCategoryDetails(data.category, year, quarter, month);
      console.log('Category details fetched:', details?.length);
      setDrillDownData(details || []);
    }
  };

  const clearFilter = (type) => {
    if (type === 'quarter') setQuarter(null);
    if (type === 'month') setMonth(null);
  };

  return (
    <div className="space-y-6" data-testid="expense-dashboard">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Year:</span>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-[120px]" data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Quarter:</span>
              <Select value={quarter ? String(quarter) : "all"} onValueChange={(v) => setQuarter(v === "all" ? null : Number(v))}>
                <SelectTrigger className="w-[150px]" data-testid="filter-quarter">
                  <SelectValue placeholder="All Quarters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quarters</SelectItem>
                  {QUARTERS.map(q => (
                    <SelectItem key={q.value} value={String(q.value)}>{q.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {quarter && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => clearFilter('quarter')}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Month:</span>
              <Select value={month ? String(month) : "all"} onValueChange={(v) => setMonth(v === "all" ? null : Number(v))}>
                <SelectTrigger className="w-[150px]" data-testid="filter-month">
                  <SelectValue placeholder="All Months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {month && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => clearFilter('month')}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <IndianRupee className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Spend</p>
                  <p className="text-2xl font-bold" data-testid="kpi-total-spend">
                    {formatCurrency(kpis?.total_spend || 0)}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-secondary/20">
                  <Receipt className="w-6 h-6 text-secondary-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Transactions</p>
                  <p className="text-2xl font-bold" data-testid="kpi-transactions">
                    {kpis?.transaction_count || 0}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-100">
                  <Award className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Top Category</p>
                  <p className="text-lg font-bold truncate max-w-[200px]" data-testid="kpi-top-category">
                    {kpis?.top_category || 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Month Over Month Trend
            </CardTitle>
            <CardDescription>Spending trend for {year}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis 
                    tick={{ fontSize: 12 }} 
                    tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="amount" fill="#0F52BA" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Spending by Category</CardTitle>
            <CardDescription>Click a category below to see transactions</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : categoryBreakdown.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart 
                    data={categoryBreakdown.slice(0, 10)} 
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis 
                      type="number" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="category" 
                      tick={{ fontSize: 11 }}
                      width={120}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Amount']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Bar 
                      dataKey="amount" 
                      radius={[0, 4, 4, 0]}
                      cursor="pointer"
                      onClick={(data) => handleCategoryClick(data)}
                    >
                      {categoryBreakdown.slice(0, 10).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                          onClick={() => handleCategoryClick(entry)}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Clickable category list for drill-down */}
                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                  {categoryBreakdown.slice(0, 10).map((entry, index) => (
                    <button
                      key={entry.category}
                      onClick={() => handleCategoryClick(entry)}
                      className="flex items-center gap-1.5 px-2 py-1 text-xs rounded-full hover:bg-muted transition-colors"
                      style={{ border: `1px solid ${CHART_COLORS[index % CHART_COLORS.length]}` }}
                      data-testid={`category-btn-${index}`}
                    >
                      <span 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      {entry.category}
                    </button>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Drill-down Table */}
      {drillDownCategory && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Transactions: {drillDownCategory}</CardTitle>
              <CardDescription>{drillDownData.length} transactions found</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setDrillDownCategory(null)}>
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Paid By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillDownData.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>{expense.date}</TableCell>
                      <TableCell className="font-medium">{expense.item_name}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[200px] truncate">
                        {expense.description || '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(expense.amount, expense.currency)}
                      </TableCell>
                      <TableCell>{expense.payment_mode}</TableCell>
                      <TableCell>{expense.paid_by}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Expenses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Top 5 Highest Value Expenses
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : topExpenses.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No expenses found</p>
          ) : (
            <div className="space-y-3">
              {topExpenses.map((expense, index) => (
                <div 
                  key={expense.id} 
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center">
                      {index + 1}
                    </Badge>
                    <div>
                      <p className="font-medium">{expense.item_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {expense.category} • {expense.date}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{formatCurrency(expense.amount, expense.currency)}</p>
                    <p className="text-xs text-muted-foreground">{expense.paid_by}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpenseDashboard;
